import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  MessageCircle,
  GraduationCap,
  Award,
  IdCard,
  Briefcase,
  Building2,
  Compass,
  FileText,
  MessagesSquare,
  Users,
  Route,
  Globe2,
  UserRound,
  Target,
  HeartHandshake,
  TrendingUp,
  Search,
  UserCheck,
  BriefcaseBusiness,
  MapPin,
} from "lucide-react";
import {
  Container,
  AxorButton,
  Reveal,
  SectionHeading,
  Overline,
  GlassCard,
  EASE,
} from "@/components/primitives";
import {
  PrismHero,
  FloatingShard,
  PrismBackground,
  PrismNodes,
  PrismOutline,
} from "@/components/Prism";
import { PartnerMarquee } from "@/components/Marquee";
import { Timeline, careerSteps } from "@/components/Timeline";
import { TestimonialCard } from "@/components/TestimonialCard";
import { JobCard } from "@/components/JobCard";
import { JobDetail } from "@/components/JobDetail";
import { CTASection } from "@/components/CTASection";
import { services } from "@/data/services";
import { testimonials } from "@/data/testimonials";
import { jobs } from "@/data/jobs";
import { waLink } from "@/config";

/* ---------------- HERO ---------------- */

const headlineLines = ["Helping Students & Professionals", "Build Their"];

const HeroReveal = ({ line, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {line}
    </motion.span>
  </span>
);

/*
 * New Hero visual:
 * Career opportunity ecosystem instead of the old large prism + floating chips.
 */
const HeroCareerVisual = () => {
  const nodes = [
    {
      icon: Search,
      label: "Career Search",
      className: "left-0 top-[8%]",
      delay: 0.3,
    },
    {
      icon: UserCheck,
      label: "Professional Guidance",
      className: "right-0 top-[25%]",
      delay: 0.45,
    },
    {
      icon: BriefcaseBusiness,
      label: "Job Opportunities",
      className: "left-[4%] bottom-[14%]",
      delay: 0.6,
    },
    {
      icon: Building2,
      label: "US Employers",
      className: "right-[2%] bottom-[5%]",
      delay: 0.75,
    },
  ];

  return (
    <div className="relative h-full w-full" aria-hidden="true">
      {/* Ambient glow */}
      <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-axor-cyan/10 blur-[100px]" />

      {/* Large geometric background */}
      <svg
        className="absolute inset-0 h-full w-full opacity-30"
        viewBox="0 0 500 420"
        fill="none"
      >
        <defs>
          <linearGradient
            id="career-network-line"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop offset="0" stopColor="#16B8D4" stopOpacity="0.05" />
            <stop offset="0.5" stopColor="#8DEEF0" stopOpacity="0.65" />
            <stop offset="1" stopColor="#16B8D4" stopOpacity="0.05" />
          </linearGradient>

          <radialGradient id="career-core-glow">
            <stop offset="0" stopColor="#32D6D0" stopOpacity="0.35" />
            <stop offset="1" stopColor="#16B8D4" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Connecting network */}
        <line
          x1="250"
          y1="210"
          x2="92"
          y2="95"
          stroke="url(#career-network-line)"
          strokeWidth="1"
        />
        <line
          x1="250"
          y1="210"
          x2="408"
          y2="130"
          stroke="url(#career-network-line)"
          strokeWidth="1"
        />
        <line
          x1="250"
          y1="210"
          x2="105"
          y2="350"
          stroke="url(#career-network-line)"
          strokeWidth="1"
        />
        <line
          x1="250"
          y1="210"
          x2="405"
          y2="370"
          stroke="url(#career-network-line)"
          strokeWidth="1"
        />

        {/* Secondary network */}
        <line
          x1="92"
          y1="95"
          x2="408"
          y2="130"
          stroke="url(#career-network-line)"
          strokeWidth="0.7"
          opacity="0.5"
        />
        <line
          x1="105"
          y1="350"
          x2="405"
          y2="370"
          stroke="url(#career-network-line)"
          strokeWidth="0.7"
          opacity="0.5"
        />

        {/* Core glow */}
        <circle
          cx="250"
          cy="210"
          r="115"
          fill="url(#career-core-glow)"
        />

        {/* Geometric core */}
        <polygon
          points="250,105 330,250 250,300 170,250"
          fill="rgba(22,184,212,0.06)"
          stroke="#8DEEF0"
          strokeWidth="0.8"
          opacity="0.65"
        />

        <polygon
          points="250,130 305,235 250,270"
          fill="#32D6D0"
          opacity="0.18"
        />

        <polygon
          points="250,130 195,235 250,270"
          fill="#26317A"
          opacity="0.35"
        />

        <line
          x1="250"
          y1="130"
          x2="250"
          y2="270"
          stroke="#8DEEF0"
          strokeWidth="1"
          opacity="0.6"
        />

        {/* Network nodes */}
        {[
          [92, 95],
          [408, 130],
          [105, 350],
          [405, 370],
        ].map(([x, y], index) => (
          <g key={`${x}-${y}`}>
            <circle
              cx={x}
              cy={y}
              r="18"
              fill="#16B8D4"
              opacity="0.08"
            />
            <circle
              cx={x}
              cy={y}
              r="4"
              fill="#8DEEF0"
              opacity="0.85"
            />
          </g>
        ))}
      </svg>

      {/* Central glass card */}
      <motion.div
        className="absolute left-1/2 top-1/2 flex h-36 w-36 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[2rem] border border-white/[0.12] bg-white/[0.045] shadow-[0_30px_100px_rgba(22,184,212,0.18)] backdrop-blur-xl"
        initial={{ opacity: 0, scale: 0.7, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{
          duration: 1,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <div className="absolute inset-3 rounded-[1.5rem] border border-axor-cyan/10" />

        <div className="relative flex flex-col items-center">
          <motion.div
            className="flex h-16 w-16 items-center justify-center rounded-2xl border border-axor-cyan/25 bg-axor-cyan/10"
            animate={{
              boxShadow: [
                "0 0 0 rgba(22,184,212,0)",
                "0 0 35px rgba(22,184,212,0.18)",
                "0 0 0 rgba(22,184,212,0)",
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <svg
              viewBox="0 0 100 100"
              className="h-10 w-10"
              fill="none"
            >
              <defs>
                <linearGradient
                  id="hero-core-left"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0" stopColor="#2A357F" />
                  <stop offset="1" stopColor="#11194F" />
                </linearGradient>

                <linearGradient
                  id="hero-core-right"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0" stopColor="#8DEEF0" />
                  <stop offset="0.55" stopColor="#32D6D0" />
                  <stop offset="1" stopColor="#16B8D4" />
                </linearGradient>
              </defs>

              <polygon
                points="50,10 16,74 50,74"
                fill="url(#hero-core-left)"
              />

              <polygon
                points="50,10 50,74 84,74"
                fill="url(#hero-core-right)"
              />

              <line
                x1="50"
                y1="10"
                x2="50"
                y2="74"
                stroke="#8DEEF0"
                strokeWidth="1"
                opacity="0.7"
              />
            </svg>
          </motion.div>

          <span className="mt-4 text-[10px] font-bold uppercase tracking-[0.28em] text-axor-white/70">
            AXOR
          </span>

          <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.18em] text-axor-cyan/70">
            Career Network
          </span>
        </div>
      </motion.div>

      {/* Floating opportunity cards */}
      {nodes.map((node) => {
        const Icon = node.icon;

        return (
          <motion.div
            key={node.label}
            className={`absolute ${node.className} flex items-center gap-2 rounded-2xl border border-white/[0.1] bg-white/[0.045] px-3 py-2.5 shadow-[0_12px_40px_rgba(0,0,0,0.18)] backdrop-blur-xl`}
            initial={{ opacity: 0, y: 15, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.7,
              delay: node.delay,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-axor-cyan/10 text-axor-cyan">
              <Icon className="h-3.5 w-3.5" />
            </span>

            <span className="whitespace-nowrap text-[10px] font-semibold text-axor-white/75">
              {node.label}
            </span>
          </motion.div>
        );
      })}

      {/* Small floating shards */}
      <motion.div
        className="absolute right-[13%] top-[5%] h-6 w-6"
        animate={{
          y: [0, -10, 0],
          rotate: [0, 8, 0],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <svg viewBox="0 0 40 40" className="h-full w-full">
          <polygon
            points="20,3 35,30 20,37 5,30"
            fill="#32D6D0"
            opacity="0.6"
          />
        </svg>
      </motion.div>

      <motion.div
        className="absolute bottom-[13%] right-[18%] h-5 w-5"
        animate={{
          y: [0, 8, 0],
          rotate: [0, -10, 0],
        }}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
      >
        <svg viewBox="0 0 40 40" className="h-full w-full">
          <polygon
            points="20,2 37,20 20,38 3,20"
            fill="#16B8D4"
            opacity="0.55"
          />
        </svg>
      </motion.div>
    </div>
  );
};

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const sx = useSpring(mx, {
    stiffness: 60,
    damping: 18,
  });

  const sy = useSpring(my, {
    stiffness: 60,
    damping: 18,
  });

  const visualX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const visualY = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const visualRotate = useTransform(sx, [-0.5, 0.5], [-2, 2]);
  const glowX = useTransform(sx, [-0.5, 0.5], [20, -20]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();

    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      className="relative min-h-screen overflow-hidden pt-32 pb-20 md:pt-40"
      data-testid="home-hero"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-axor-hero via-axor-deep to-axor-hero" />

      <div className="light-rays absolute inset-0" />

      {/* Animated aurora glow */}
      <motion.div
        className="pointer-events-none absolute right-[10%] top-1/4 h-[440px] w-[440px] rounded-full bg-axor-cyan/[0.12] blur-[120px]"
        style={{ x: glowX }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.5, 0.85, 0.5],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Subtle dot grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.25] [background-image:radial-gradient(rgba(141,238,240,0.12)_1px,transparent_1px)] [background-size:38px_38px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />

      <PrismBackground />

      <Container className="relative grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Overline>
              US Career &amp; Recruitment Technology
            </Overline>
          </motion.div>

          <h1 className="mt-6 text-4xl font-semibold leading-[1.05] tracking-tight text-axor-white sm:text-5xl lg:text-6xl">
            <HeroReveal
              line={headlineLines[0]}
              delay={0.15}
            />

            <span className="flex flex-wrap items-baseline gap-x-3 overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1.1,
                  ease: EASE,
                  delay: 0.3,
                }}
              >
                {headlineLines[1]}
              </motion.span>

              <motion.span
                className="text-gradient-cyan"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1.1,
                  ease: EASE,
                  delay: 0.42,
                }}
              >
                Future in America.
              </motion.span>
            </span>
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-lg leading-relaxed text-axor-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.9,
              delay: 0.7,
            }}
          >
            Axor Technologies connects students, graduates, Green Card
            holders, and professionals across the United States with career
            opportunities, professional guidance, and the right employers.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.9,
              delay: 0.85,
            }}
          >
            <AxorButton
              to="/jobs"
              icon={ArrowUpRight}
              data-testid="hero-explore-jobs"
            >
              Explore Jobs
            </AxorButton>

            <AxorButton
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              icon={MessageCircle}
              data-testid="hero-talk-axor"
            >
              Talk To Axor
            </AxorButton>
          </motion.div>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 1,
              delay: 1.1,
            }}
          >
            <p className="text-sm font-medium tracking-wide text-axor-white/50">
              Your career. Your opportunity. Your next move.
            </p>

            <div className="hidden h-8 w-px bg-white/10 sm:block" />

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold uppercase tracking-[0.14em] text-axor-white/40">
              <span>Students</span>
              <span className="text-axor-cyan/50">•</span>
              <span>Professionals</span>
              <span className="text-axor-cyan/50">•</span>
              <span>Employers</span>
            </div>
          </motion.div>
        </div>

        {/* NEW HERO CAREER NETWORK VISUAL */}
        <motion.div
          className="relative mx-auto h-[340px] w-full max-w-lg lg:h-[430px]"
          style={{
            x: visualX,
            y: visualY,
            rotate: visualRotate,
          }}
        >
          <HeroCareerVisual />
        </motion.div>
      </Container>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 1.6,
          duration: 0.8,
        }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-axor-white/40">
          Scroll
        </span>

        <div className="flex h-9 w-5 items-start justify-center rounded-full border border-white/15 p-1">
          <motion.span
            className="h-1.5 w-1 rounded-full bg-axor-cyan"
            animate={{ y: [0, 10, 0] }}
            transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      </motion.div>
    </section>
  );
};

/* ---------------- TECH ECOSYSTEM ---------------- */

const TechEcosystem = () => (
  <section
    className="relative overflow-hidden border-y border-white/[0.05] bg-axor-hero py-20 md:py-24"
    data-testid="tech-ecosystem"
  >
    <PrismBackground />

    <Container className="relative">
      <div className="mx-auto max-w-2xl text-center">
        <Reveal>
          <Overline className="justify-center">
            Technology Ecosystem
          </Overline>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-5 text-3xl font-semibold tracking-tight text-axor-white sm:text-4xl">
            Where Ambition Meets Opportunity.
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mx-auto mt-4 max-w-xl text-base text-axor-text">
            Building pathways toward opportunities across leading technology
            companies and organizations.
          </p>
        </Reveal>
      </div>
    </Container>

    <div className="relative mt-14">
      <PartnerMarquee />
    </div>
  </section>
);

/* ---------------- WHO WE SERVE ---------------- */

const audiences = [
  {
    icon: GraduationCap,
    title: "Students",
    copy: "Explore opportunities and build your professional foundation.",
  },
  {
    icon: Award,
    title: "Graduates",
    copy: "Turn your education into meaningful career opportunities.",
  },
  {
    icon: IdCard,
    title: "Green Card Holders",
    copy: "Discover opportunities aligned with your skills and experience.",
  },
  {
    icon: Briefcase,
    title: "Professionals",
    copy: "Take the next step in your career with strategic guidance.",
  },
  {
    icon: Building2,
    title: "Employers",
    copy: "Connect with qualified talent across the United States.",
  },
];

const WhoWeServe = () => (
  <section
    className="relative overflow-hidden py-24 md:py-32"
    data-testid="who-we-serve"
  >
    <PrismBackground />

    <Container className="relative">
      <SectionHeading
        overline="Who We Serve"
        title="Built For Every Stage Of Your Career."
        highlight="Every Stage"
      />

      <div className="mt-14 grid gap-6 md:grid-cols-6">
        {audiences.map((a, i) => (
          <motion.div
            key={a.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{
              once: true,
              margin: "-60px",
            }}
            transition={{
              duration: 0.7,
              ease: EASE,
              delay: i * 0.08,
            }}
            className={
              i === 0
                ? "md:col-span-2 md:row-span-2"
                : "md:col-span-2"
            }
          >
            <GlassCard
              className={`group relative flex h-full flex-col overflow-hidden p-7 ${
                i === 0 ? "md:min-h-[300px]" : ""
              }`}
            >
              {i === 0 && (
                <PrismOutline
                  className="pointer-events-none absolute -right-6 -top-8 opacity-25 transition-opacity duration-500 group-hover:opacity-40"
                  size={140}
                />
              )}

              <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-axor-cyan/25 bg-axor-cyan/10 text-axor-cyan transition-transform duration-300 group-hover:scale-110">
                <a.icon className="h-6 w-6" />
              </span>

              <div
                className={
                  i === 0
                    ? "mt-auto pt-10"
                    : "mt-6"
                }
              >
                <h3
                  className={`font-semibold tracking-tight text-axor-white ${
                    i === 0
                      ? "text-3xl"
                      : "text-xl"
                  }`}
                >
                  {a.title}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-axor-text">
                  {a.copy}
                </p>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Container>
  </section>
);

/* ---------------- MANIFESTO / INTRO ---------------- */

const Manifesto = () => (
  <section
    className="relative overflow-hidden bg-axor-deep py-24 md:py-32"
    data-testid="home-intro"
  >
    <PrismBackground />

    <Container className="relative grid items-center gap-14 lg:grid-cols-[1fr_0.8fr]">
      <div>
        <Reveal>
          <Overline>More Than Recruitment</Overline>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-6 text-4xl font-semibold leading-[1.1] tracking-tight text-axor-white sm:text-5xl">
            More Than Recruitment.
            <br />
            <span className="text-gradient-cyan">
              A Career Partner.
            </span>
          </h2>
        </Reveal>

        <div className="mt-8 space-y-6">
          <Reveal delay={0.2}>
            <p className="max-w-xl text-lg leading-relaxed text-axor-text">
              At Axor Technologies, we believe career growth takes more than
              simply finding a job. It takes the right direction, preparation,
              opportunity and support.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <p className="max-w-xl text-lg leading-relaxed text-axor-text">
              From students beginning their professional journey to experienced
              professionals looking for their next opportunity, Axor helps
              people move forward with confidence.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.4}>
          <AxorButton
            to="/about"
            variant="secondary"
            icon={ArrowUpRight}
            className="mt-9"
            data-testid="intro-discover"
          >
            Discover Axor
          </AxorButton>
        </Reveal>
      </div>

      {/* Asymmetric prism composition */}
      <div className="relative flex justify-center">
        <div className="absolute -inset-6 rounded-full bg-axor-cyan/10 blur-3xl" />

        <PrismHero className="relative w-40" />

        <span className="absolute -left-6 top-8 select-none text-8xl font-bold text-white/[0.04]">
          01
        </span>
      </div>
    </Container>
  </section>
);

/* ---------------- HOME SERVICES ---------------- */

const HomeServices = () => (
  <section
    className="relative overflow-hidden py-24 md:py-32"
    data-testid="home-services"
  >
    <PrismBackground />

    <Container className="relative">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading
          overline="What We Do"
          title="Everything You Need To Move Forward."
          highlight="Move Forward"
        />

        <AxorButton
          to="/services"
          variant="ghost"
          icon={ArrowRight}
          className="shrink-0"
          data-testid="home-services-cta"
        >
          Explore Our Services
        </AxorButton>
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-12">
        {services.slice(0, 5).map((s, i) => {
          const spans = [
            "md:col-span-7",
            "md:col-span-5",
            "md:col-span-4",
            "md:col-span-4",
            "md:col-span-4",
          ];

          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
                margin: "-60px",
              }}
              transition={{
                duration: 0.6,
                ease: EASE,
                delay: (i % 3) * 0.1,
              }}
              className={spans[i]}
            >
              <GlassCard className="group flex h-full items-start gap-5 p-7">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-axor-cyan/25 bg-axor-cyan/10 text-axor-cyan transition-transform duration-300 group-hover:scale-110">
                  <s.icon className="h-6 w-6" />
                </span>

                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-xl font-semibold tracking-tight text-axor-white">
                      {s.title}
                    </h3>

                    <ArrowUpRight className="h-5 w-5 shrink-0 text-axor-text transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:text-axor-cyan" />
                  </div>

                  <p className="mt-2 text-sm leading-relaxed text-axor-text">
                    {s.short}
                  </p>
                </div>
              </GlassCard>
            </motion.div>
          );
        })}
      </div>
    </Container>
  </section>
);

/* ---------------- WHY AXOR ---------------- */

const reasons = [
  {
    icon: Globe2,
    title: "US Market Expertise",
    copy: "Understand the realities of today's American employment landscape.",
  },
  {
    icon: UserRound,
    title: "Personalized Guidance",
    copy: "Your career path is unique. Our approach reflects that.",
  },
  {
    icon: Target,
    title: "Opportunity Focused",
    copy: "Connect your skills, ambitions and opportunities.",
  },
  {
    icon: HeartHandshake,
    title: "Human Support",
    copy: "Technology helps us move faster. People help us move better.",
  },
  {
    icon: TrendingUp,
    title: "Career-Long Perspective",
    copy: "Focus on meaningful professional growth, not just quick placements.",
  },
];

const WhyAxor = () => (
  <section
    className="relative overflow-hidden bg-axor-deep py-24 md:py-32"
    data-testid="why-axor"
  >
    <PrismBackground />

    <Container className="relative grid gap-14 lg:grid-cols-[0.85fr_1.15fr]">
      <div className="lg:sticky lg:top-32 lg:self-start">
        <SectionHeading
          overline="Why Axor"
          title="Built Around Your Success."
          highlight="Your Success"
          description="Technology-driven, human-led. We combine market understanding with a personal approach to help you move forward."
        />

        <div className="relative mt-12 hidden h-64 lg:block">
          <div className="absolute inset-0 rounded-full bg-axor-cyan/10 blur-3xl" />

          <PrismNodes className="relative" />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {reasons.map((r, i) => (
          <motion.div
            key={r.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              margin: "-60px",
            }}
            transition={{
              duration: 0.6,
              ease: EASE,
              delay: (i % 2) * 0.1,
            }}
            className={
              i === 0
                ? "sm:col-span-2"
                : ""
            }
          >
            <GlassCard className="group flex h-full flex-col p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-axor-cyan/25 bg-axor-cyan/10 text-axor-cyan">
                <r.icon className="h-5 w-5" />
              </span>

              <h3 className="mt-5 text-lg font-semibold tracking-tight text-axor-white">
                {r.title}
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-axor-text">
                {r.copy}
              </p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Container>
  </section>
);

/* ---------------- CAREER JOURNEY ---------------- */

const CareerJourney = () => (
  <section
    className="relative overflow-hidden py-24 md:py-32"
    data-testid="career-journey"
  >
    <PrismBackground />

    <Container className="relative">
      <SectionHeading
        overline="How It Works"
        title="Your Career Journey, Simplified."
        highlight="Simplified"
        align="center"
        className="mx-auto items-center text-center"
      />

      <div className="mt-16">
        <Timeline steps={careerSteps} />
      </div>
    </Container>
  </section>
);

/* ---------------- FEATURED JOBS ---------------- */

const FeaturedJobs = ({ onView }) => {
  const featured = jobs
    .filter((j) => j.featured)
    .slice(0, 3);

  return (
    <section
      className="relative overflow-hidden bg-axor-deep py-24 md:py-32"
      data-testid="featured-jobs"
    >
      <PrismBackground />

      <Container className="relative">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            overline="Open Roles"
            title="Your Next Opportunity Could Be Here."
            highlight="Opportunity"
          />

          <AxorButton
            to="/jobs"
            variant="ghost"
            icon={ArrowRight}
            className="shrink-0"
            data-testid="featured-explore-all"
          >
            Explore All Jobs
          </AxorButton>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {featured.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onView={onView}
            />
          ))}
        </div>
      </Container>
    </section>
  );
};

/* ---------------- TESTIMONIALS ---------------- */

const Testimonials = () => (
  <section
    className="relative overflow-hidden py-24 md:py-32"
    data-testid="testimonials"
  >
    <PrismBackground />

    <Container className="relative">
      <SectionHeading
        overline="Testimonials"
        title="Real Journeys. Real Ambition."
        highlight="Real Ambition"
      />
    </Container>

    {/* Gentle gliding marquee so every voice gets seen */}
    <div
      className="marquee-pause relative mt-14"
      data-testid="testimonials-marquee"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-axor-hero to-transparent md:w-32" />

      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-axor-hero to-transparent md:w-32" />

      <div
        className="marquee-track flex w-max items-stretch gap-6 animate-marquee"
        style={{ animationDuration: "55s" }}
      >
        {[...testimonials, ...testimonials].map(
          (t, i) => (
            <div
              key={`${t.name}-${i}`}
              className="w-[300px] shrink-0 sm:w-[380px]"
            >
              <TestimonialCard
                item={t}
                className="h-full"
              />
            </div>
          ),
        )}
      </div>
    </div>
  </section>
);

/* ---------------- EDITORIAL MARQUEE ---------------- */

const editorialWords = [
  "Career",
  "Opportunity",
  "Professional Growth",
  "Recruitment",
  "US Employers",
  "Technology",
];

const EditorialMarquee = () => (
  <section
    className="relative overflow-hidden border-y border-white/[0.05] bg-axor-hero py-10 md:py-14"
    data-testid="editorial-marquee"
    aria-hidden="true"
  >
    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-gradient-to-r from-axor-hero to-transparent" />

    <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-gradient-to-l from-axor-hero to-transparent" />

    <div className="flex w-max animate-marquee items-center gap-10">
      {[
        ...editorialWords,
        ...editorialWords,
        ...editorialWords,
      ].map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="flex items-center gap-10"
        >
          <span
            className={`text-3xl font-semibold tracking-tight md:text-5xl ${
              i % 2 === 0
                ? "text-axor-white/80"
                : "text-transparent [-webkit-text-stroke:1px_rgba(141,238,240,0.35)]"
            }`}
          >
            {w}
          </span>

          <svg
            viewBox="0 0 40 40"
            className="h-4 w-4 shrink-0"
          >
            <polygon
              points="20,4 36,34 4,34"
              fill="#16B8D4"
              opacity="0.6"
            />
          </svg>
        </span>
      ))}
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */

export default function Home() {
  const [selectedJob, setSelectedJob] =
    React.useState(null);

  return (
    <>
      <Hero />

      <TechEcosystem />

      <WhoWeServe />

      <Manifesto />

      <HomeServices />

      <WhyAxor />

      <CareerJourney />

      <EditorialMarquee />

      <FeaturedJobs
        onView={setSelectedJob}
      />

      <Testimonials />

      <CTASection />

      <JobDetail
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
      />
    </>
  );
}
